/* Wishlist (Swish / Wishlist King) — theme-side placement.

   Wishlist King injects its elements at runtime from scripts hosted by Swish at
   cdn.appmate.io/themecode/40a237-a1/staging-1/, which cannot be edited from the
   theme:

     product-page-buttons.js  appends <wishlist-button-product> into
                              `.product__info .product__trustpilot`
     header-link.js           inserts <wishlist-link> before `.cart-drawer-button`
                              without a link, so the anchor falls back to the
                              login URL instead of the wishlist page

   This file corrects both from the theme side. Styling lives in
   assets/wishlist-custom.css.

   Note that the app is configured to load lazily (appLoading: LAZY), so its
   elements can appear at any point after page load, and again whenever the
   Section Rendering API re-renders the product info column. Everything below is
   therefore driven by a MutationObserver and is safe to run repeatedly. */

(function () {
  'use strict';

  var GALLERY = '.product__gallery-container';
  var BUTTON = 'wishlist-button-product';
  var LINK = 'wishlist-link';
  var SWIPE_THRESHOLD = 8;

  function wishlistPath() {
    var app = window.WishlistKing;
    var path = app && app.routes && app.routes.wishlistUrl;

    if (!path && app && app.settings && app.settings.general) {
      path = app.settings.general.wishlistPath;
    }

    return path || '/apps/wishlist';
  }

  /* Distance from the start of the scroller's content to the slide nearest to
     where the drag ended, so the gallery can be snapped back onto a slide. */
  function nearestSlideOffset(scroller) {
    var slides = scroller.querySelectorAll('.product__media');
    var origin = scroller.getBoundingClientRect().left;
    var current = scroller.scrollLeft;
    var best = current;
    var shortest = Infinity;

    for (var i = 0; i < slides.length; i++) {
      var offset = slides[i].getBoundingClientRect().left - origin + current;
      var distance = Math.abs(offset - current);
      if (distance < shortest) {
        shortest = distance;
        best = offset;
      }
    }

    return best;
  }

  /* The gallery slider is a native horizontal scroller (overflow-x: auto,
     scroll-snap). The heart is anchored to the gallery container, which sits
     outside that scroller, so a swipe starting on the heart would otherwise do
     nothing. Forward the horizontal part of the gesture to the slider, and
     swallow the tap that ends such a swipe so it does not toggle the wishlist. */
  function forwardSwipes(button) {
    if (button.dataset.wishlistSwipeBound) return;
    button.dataset.wishlistSwipeBound = 'true';

    var startX = 0;
    var startY = 0;
    var startScrollLeft = 0;
    var scroller = null;
    var decided = false;
    var panning = false;
    var panEndedAt = 0;

    button.addEventListener('touchstart', function (event) {
      decided = false;
      panning = false;
      scroller = null;

      if (event.touches.length !== 1) return;

      var gallery = button.closest(GALLERY);
      var candidate = gallery && gallery.querySelector('slider-element');
      if (!candidate || candidate.scrollWidth <= candidate.clientWidth) return;

      scroller = candidate;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      startScrollLeft = scroller.scrollLeft;
    }, { passive: true });

    button.addEventListener('touchmove', function (event) {
      if (!scroller || event.touches.length !== 1) return;

      var deltaX = event.touches[0].clientX - startX;
      var deltaY = event.touches[0].clientY - startY;

      if (!decided) {
        if (Math.abs(deltaX) < SWIPE_THRESHOLD && Math.abs(deltaY) < SWIPE_THRESHOLD) return;
        decided = true;
        panning = Math.abs(deltaX) > Math.abs(deltaY);
        if (!panning) return;

        button.setAttribute('data-wishlist-panning', '');
        // Two of the slider's own styles fight a programmatic drag:
        // `scroll-snap-type: x mandatory` pulls every scrollLeft straight back
        // onto the current slide, and `scroll-behavior: smooth` turns each one
        // into a fresh animation so the gallery lags far behind the finger.
        // Both are suspended for the drag and restored when it ends.
        scroller.style.scrollSnapType = 'none';
        scroller.style.scrollBehavior = 'auto';
      }

      // A vertical gesture is left to the browser so the page still scrolls.
      if (panning) scroller.scrollLeft = startScrollLeft - deltaX;
    }, { passive: true });

    function endGesture() {
      if (!panning) return;

      var slider = scroller;
      panning = false;
      scroller = null;
      panEndedAt = Date.now();
      button.removeAttribute('data-wishlist-panning');

      var target = nearestSlideOffset(slider);
      slider.style.scrollBehavior = '';
      slider.scrollTo({ left: target, behavior: 'smooth' });

      // Snapping stays off until the glide has settled, so it cannot cut it short.
      setTimeout(function () {
        slider.style.scrollSnapType = '';
      }, 400);
    }

    button.addEventListener('touchend', endGesture);
    button.addEventListener('touchcancel', endGesture);

    // A swipe ends in a click. Suppress only that one, never a real tap.
    button.addEventListener('click', function (event) {
      if (Date.now() - panEndedAt > 500) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  /* Move the injected button out of the Trustpilot row and into the gallery.
     Products without media have no gallery container; there the button is left
     where the app put it. */
  function placeGalleryButton() {
    var gallery = document.querySelector(GALLERY);
    if (!gallery) return;

    var buttons = document.querySelectorAll(BUTTON);

    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      if (button.parentElement === gallery) continue;

      // A re-render injects a fresh button carrying the current variant; the
      // one already in the gallery is then stale.
      var stale = gallery.querySelector(':scope > ' + BUTTON);
      if (stale && stale !== button) stale.remove();

      gallery.appendChild(button);
      forwardSwipes(button);
    }
  }

  function setHeaderLinkHref() {
    var links = document.querySelectorAll(LINK);
    var href = wishlistPath();

    for (var i = 0; i < links.length; i++) {
      var anchor = links[i].querySelector('a');
      if (!anchor || anchor.getAttribute('href') === href) continue;
      anchor.setAttribute('href', href);
    }
  }

  function apply() {
    placeGalleryButton();
    setHeaderLinkHref();
  }

  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      apply();
    });
  }

  apply();

  // childList only: setting href must not feed back into the observer.
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true
  });
})();
